import type { Text } from '@kkf/design'
import type { ApiAppeal } from '../api'

/**
 * Donation causes — the shape the pages draw, and how an appeal from the API becomes one.
 *
 * ## The appeals are not here any more (2026-09-25)
 *
 * This file held the two appeals — the USD $1 million capital ask and the USD $2,000 to fly
 * the library's books in from Vietnam — as hand-written records, from 2026-09-19 until the
 * API could hold them. They are rows in `app_api`'s `appeals` table now, seeded by the
 * migration that created it (`20260925_120000_a3f9d2c4e871_donations.py`, which carries the
 * words verbatim), and they are written and closed in Steward's Donations screen.
 *
 * **Moved, not copied**: nothing here repeats them, so there is never a moment when the site
 * and the database both claim to be where the capital ask is written down. What stays is the
 * view shape the two pages were already built against, and `toCause`, which is the one place
 * the API's shape becomes it.
 *
 * `data/seo.ts` still names the two seeded slugs by hand, for the reason written there: it is
 * loaded by Node at build time, where there is no API to ask.
 *
 * ## Nothing invented, and now nothing to invent
 *
 * `raised`, `progress` and `donations` are the API's figures, summed from **completed**
 * payments only. There is no payment provider yet, so every one of them is zero — and zero
 * is the true figure rather than a placeholder, which is what these three fields said in
 * words while they were typed in here.
 */

/**
 * Whether an appeal is still open. `past` is the API's `closed` — the directory's second tab.
 */
export type DonationStatus = 'active' | 'past'

export interface DonationCause {
  slug: string
  status: DonationStatus
  /**
   * How many people have given — the API's `gift_count`, which counts donations with a
   * completed payment. Zero until PayPal is wired, and the panel reads zero as "Be the first
   * to give" rather than as a digit.
   */
  donations: number
  /** Every photograph for this appeal, lead first. A strip shows only with two or more. */
  images: string[]
  /** A film for the lightbox, only where the Foundation has said one belongs to the appeal. */
  video?: string
  image: string
  /** Only set where the image is a real photograph somebody described. */
  imageAlt?: Text
  tag: Text
  title: Text
  /** Card summary. */
  descr: Text
  /** Details-page body. The first paragraph is the directory's collapsed panel. */
  body: Text[]
  /** Whole percent of the goal received, 0–100. From completed payments only. */
  progress: number
  /** What has been received, in `currency`. A number for formatting — never added up here. */
  raised: number
  goal: number
  currency: string
}

/**
 * An appeal from the API, as the pages draw it.
 *
 * `progress` is rounded **down**: a bar at 100% says the appeal is finished, and $1,995 of
 * $2,000 is not finished. It is capped at 100 because an appeal can be over-given and a bar
 * wider than its track is a layout bug, not good news.
 */
export function toCause(appeal: ApiAppeal): DonationCause {
  const raised = Number(appeal.raised_amount) || 0
  const goal = Number(appeal.goal_amount) || 0
  const images = appeal.images?.length ? appeal.images : [appeal.image]
  return {
    slug: appeal.slug,
    status: appeal.status === 'closed' ? 'past' : 'active',
    donations: appeal.gift_count,
    images,
    video: appeal.video ?? undefined,
    image: appeal.image,
    imageAlt: appeal.image_alt ?? undefined,
    tag: appeal.tag,
    title: appeal.title,
    descr: appeal.summary,
    body: appeal.body,
    progress: goal > 0 ? Math.min(100, Math.floor((raised / goal) * 100)) : 0,
    raised,
    goal,
    currency: appeal.currency,
  }
}

/**
 * Money, in the reader's language. **`intlLocale`, never `locale`** — a short code handed to
 * `Intl` does not throw, it formats in the default locale. Whole units where the figure is
 * whole, so the goal reads "$2,000" as the appeal's own words do, not "$2,000.00".
 */
export function formatMoney(amount: number, currency: string, intlLocale: string): string {
  try {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency,
      // "$2,000", not "US$2,000": the English locale the site resolves to is not en-US, and
      // its default symbol for dollars is the disambiguated one. The appeals' own words say
      // "$", and the figure beside them must match.
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}
